import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Briefcase, Users, ArrowRight, Plus } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, ContractWithParties } from '@hirenfire/shared';

interface AgencyStats {
  activeContracts: number;
  postedJobs: number;
  totalSpending: number;
}

interface RecentApplication {
  id: string;
  jobTitle: string;
  workerName: string;
  status: string;
  createdAt: string;
}

export function AgencyDashboard() {
  const [stats, setStats] = useState<AgencyStats>({
    activeContracts: 0,
    postedJobs: 0,
    totalSpending: 0,
  });
  const [applications, setApplications] = useState<RecentApplication[]>([]);
  const [contracts, setContracts] = useState<ContractWithParties[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsRes, appsRes, contractsRes] = await Promise.all([
          api.get<ApiResponse<AgencyStats>>('/dashboard/agency/stats'),
          api.get<ApiResponse<RecentApplication[]>>('/applications/received?limit=5'),
          api.get<ApiResponse<ContractWithParties[]>>('/contracts?status=ACTIVE&limit=5'),
        ]);
        if (statsRes.data.data) setStats(statsRes.data.data);
        if (appsRes.data.data) setApplications(appsRes.data.data);
        if (contractsRes.data.data) setContracts(contractsRes.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Agency Dashboard</h1>
          <p className="text-gray-400 mt-1">Manage your hiring pipeline</p>
        </div>
        <Link to="/jobs/create">
          <Button leftIcon={<Plus className="w-4 h-4" />}>Post a Job</Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Active Contracts</p>
                <p className="text-2xl font-bold text-white">{stats.activeContracts}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Posted Jobs</p>
                <p className="text-2xl font-bold text-white">{stats.postedJobs}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-warning-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-warning-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Spending</p>
                <p className="text-2xl font-bold text-white">
                  ${stats.totalSpending.toLocaleString()}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader action={
            <Link to="/applications">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                View All
              </Button>
            </Link>
          }>
            Recent Applications
          </CardHeader>
          <CardBody>
            {applications.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent applications</p>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-dark-700/50"
                  >
                    <div>
                      <p className="font-medium text-white text-sm">{app.workerName}</p>
                      <p className="text-xs text-gray-400">for {app.jobTitle}</p>
                    </div>
                    <Badge
                      variant={
                        app.status === 'PENDING' ? 'warning' :
                        app.status === 'ACCEPTED' ? 'success' : 'error'
                      }
                    >
                      {app.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader action={
            <Link to="/contracts">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                View All
              </Button>
            </Link>
          }>
            Active Contracts
          </CardHeader>
          <CardBody>
            {contracts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active contracts</p>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => (
                  <Link
                    key={contract.id}
                    to={`/contracts/${contract.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-white text-sm">{contract.job.title}</p>
                      <p className="text-xs text-gray-400">with {contract.worker.displayName}</p>
                    </div>
                    <p className="text-sm font-semibold text-success-400">
                      ${contract.totalAmount}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
